import type Board from '@highcharts/dashboards/es-modules/Dashboards/Board';
import type { Options as ComponentOptions } from '@highcharts/dashboards/es-modules/Dashboards/Components/Component.js';
import type { Options as EditableOption } from '@highcharts/dashboards/es-modules/Dashboards/Components/EditableOptions.js';
import type { Options as HighchartsComponentOptions } from '@highcharts/dashboards/es-modules/Dashboards/Components/HighchartsComponent/HighchartsComponentOptions.js';
import type SidebarPopup from '@highcharts/dashboards/es-modules/Dashboards/EditMode/SidebarPopup';
import type Cell from '@highcharts/dashboards/es-modules/Dashboards/Layout/Cell.js';
import { diffObjects, merge } from '@highcharts/dashboards/es-modules/Shared/Utilities.js';
import world from '@highcharts/map-collection/custom/world.topo.json';
import { Dashboards, Highcharts } from '../dashboards-core';
import { isChartTypeOption } from './dashboard-chart-types';

// The module exports `diffObjects` at runtime but Highcharts marks it internal, so its shipped
// type defs leave it out. This declares the signature the implementation actually has.
declare module '@highcharts/dashboards/es-modules/Shared/Utilities.js' {
    export function diffObjects<T extends object>(newer: T, older: object): Partial<T>;
}

/** The component type a map cell is registered and stored under. Saved boards keep it forever. */
export const DASHBOARD_MAP_TYPE = 'HighchartsMap';

/** The Highcharts factory that draws geographic charts. A cell built with it is a map cell. */
const MAP_CHART_CONSTRUCTOR = 'mapChart';

/** The series type that draws each country as a shape. */
const MAP_SERIES_TYPE = 'map';

/** The outline's key, stored by every map cell. Never rename it, saved boards keep it forever. */
const WORLD_MAP = 'world';

/** What a saved map cell holds. */
export type DashboardMapOptions = Omit<HighchartsComponentOptions, 'type'> & {
    type: typeof DASHBOARD_MAP_TYPE;
};

type DashboardMapComponentClass = Omit<typeof DashboardMapComponent, 'prototype'> & {
    prototype: Omit<DashboardMapComponent, 'options'> & {
        options: HighchartsComponentOptions | DashboardMapOptions;
    };
};

declare module '@highcharts/dashboards/es-modules/Dashboards/Components/ComponentType' {
    interface ComponentTypeRegistry {
        HighchartsMap: DashboardMapComponentClass;
    }
}

export function registerMapOutlines(): void {
    Highcharts.maps[WORLD_MAP] = world;
}

/** A chart cell drawn by the map factory. */
export class DashboardMapComponent extends Dashboards.HighchartsComponent {
    static override defaultOptions = merge(Dashboards.HighchartsComponent.defaultOptions, {
        chartConstructor: MAP_CHART_CONSTRUCTOR,
        chartOptions: {
            chart: { map: WORLD_MAP, type: MAP_SERIES_TYPE },
            colorAxis: { min: 0 },
            mapNavigation: { enabled: true },
        },
        editableOptions: withoutChartTypeOption(
            Dashboards.HighchartsComponent.defaultOptions.editableOptions ?? []
        ),
    });

    constructor(cell: Cell, options: Partial<HighchartsComponentOptions>, board?: Board) {
        super(cell, merge(DashboardMapComponent.defaultOptions, options), board);
        this.type = DASHBOARD_MAP_TYPE;
    }

    override getOptionsOnDrop(sidebar: SidebarPopup): Partial<HighchartsComponentOptions> {
        return merge(super.getOptionsOnDrop(sidebar), {
            type: DASHBOARD_MAP_TYPE,
            chartOptions: { chart: { type: MAP_SERIES_TYPE } },
        });
    }

    override updateSeries(): void {
        for (const series of [...(this.chart?.series ?? [])]) {
            series.remove(false);
        }
        super.updateSeries();
    }

    getOptions(): Partial<ComponentOptions> {
        return {
            ...diffObjects(this.options, DashboardMapComponent.defaultOptions),
            type: DASHBOARD_MAP_TYPE,
        };
    }
}

/** The same settings fields without the chart type select, for a cell whose chart has no type to pick. */
function withoutChartTypeOption(options: readonly EditableOption[]): EditableOption[] {
    return options.flatMap((option) => {
        if (isChartTypeOption(option)) {
            return [];
        }
        const nested = option.nestedOptions;
        if (nested === undefined) {
            return [option];
        }
        return [
            {
                ...option,
                nestedOptions: nested.map((group) => ({
                    ...group,
                    options: withoutChartTypeOption(group.options),
                })),
            },
        ];
    });
}
